// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TySubscriptionConfig, TySubscriptionPeriod } from "@/types";
import { formatBytes, formatMoney } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "../PlanRequestPopup/styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyStorageRequestDetails = {
    config: TySubscriptionConfig;
    /** The live period's, because a pack costs what that period costs. */
    period: TySubscriptionPeriod;
    /** What the plan allows today, so the form can say what it will allow after. */
    currentAllowed: number;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * Buying room mid-period. Storage is the one limit a paid plan has, so running into it is the one
 * way a paying company stops being able to work — and the fix has to be reachable from inside the
 * app rather than through a conversation.
 *
 * It asks, it does not buy. The packs are written `pending` and **grant nothing** until an admin
 * confirms the transfer, exactly like the period itself, because it is the same money arriving the
 * same way. The copy says so rather than implying the space is already there.
 *
 * It borrows `PlanRequestPopup`'s stylesheet rather than copying it: the stepper, the breakdown and
 * the total are the same three pieces, and two stylesheets would drift the first time one changed.
 */
export default function StorageRequestPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { config, period, currentAllowed, onDone } = details as TyStorageRequestDetails;

    const [count, setCount] = useState(1);
    const { loading, error, succeededAt, invoke } = useFetch("/subscription/storage", { method: "POST" }, false);

    useEffect(() => {
        if (!succeededAt) return;
        onDone();
        onClose();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const unitPrice = config.storageAddon[period];
    const total = count * unitPrice;
    const GB = 1024 * 1024 * 1024;

    return (
        <section className="side-form side-form--center plan-request">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.subscription.buyStorageTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <p className="plan-request__lead">{t.subscription.buyStorageHint}</p>

                <div className="plan-request__addon">
                    <div className="plan-request__addon-text">
                        <span className="plan-request__addon-title">{t.subscription.packs}</span>
                        <span className="plan-request__addon-hint">
                            {t.subscription.extraStorageHint
                                .replace("{gb}", String(config.storageAddon.gb))
                                .replace("{price}", formatMoney(unitPrice))
                                .replace("{currency}", t.properties.currency)}
                        </span>
                    </div>

                    <div className="plan-request__stepper">
                        <button
                            type="button"
                            className="plan-request__step"
                            onClick={() => setCount((value) => Math.max(1, value - 1))}
                            disabled={loading || count <= 1}
                            aria-label="-"
                        >
                            −
                        </button>
                        <span className="plan-request__count">{count}</span>
                        <button
                            type="button"
                            className="plan-request__step"
                            onClick={() => setCount((value) => Math.min(config.addonMax, value + 1))}
                            disabled={loading || count >= config.addonMax}
                            aria-label="+"
                        >
                            +
                        </button>
                    </div>
                </div>

                <div className="plan-request__lines">
                    <div className="plan-request__line plan-request__line--quiet">
                        <span>{t.subscription.storage}</span>
                        {/* What they have now and what it becomes — the whole reason they are here. */}
                        <span>
                            {formatBytes(currentAllowed)} → {formatBytes(currentAllowed + count * config.storageAddon.gb * GB)}
                        </span>
                    </div>

                    <div className="plan-request__line plan-request__line--total">
                        <span>{t.subscription.total}</span>
                        <span>
                            {formatMoney(total)} {t.properties.currency}
                        </span>
                    </div>
                </div>

                <p className="side-form__hint">{t.subscription.reviewHint}</p>
                {error && <p className="side-form__error">{error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={() => invoke({ method: "POST", data: { count } })} disabled={loading}>
                    {loading ? t.common.loading : t.subscription.request}
                </button>
            </footer>
        </section>
    );
}
